import React, { FC, useCallback } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import Button from '../../../../components/common/Button';
import Input from '../../../../components/common/Input';
import Accordion from '../../../../components/elements/Accordion';
import Confirmation from '../../../../components/elements/Confirmation';
import { theme } from '../../../../global';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useModal } from '../../../../hooks/useModal';
import { useAppSelector } from '../../../../hooks/useSelector';
import { IClassicDuelState } from '../../../../lib/interfaces/IClassicDuel';
import { getClassicDuel } from '../../../../store/selectors/settings.selector';
import { ClassicDuelInitialState, settingsActions } from '../../../../store/slices/settings.slice';
import { teamActions } from '../../../../store/slices/team.slice';

import {
  RESET_SETTINGS_ABORT_TEXT,
  RESET_SETTINGS_CONFIRM_TEXT,
  RESET_SETTINGS_CONFIRMATION,
} from './config';
import * as S from './styles';

export interface IClassicDuelFormProps {
  toggleFlyout: () => void;
}

const ClassicDuelForm: FC<IClassicDuelFormProps> = ({ toggleFlyout }) => {
  const dispatch = useAppDispatch();
  const initialState = useAppSelector(getClassicDuel);
  const [params, setParams] = useSearchParams();
  const { isActive, toggleHandler: toggleConfirmation } = useModal({ handler: toggleFlyout });
  const { register, handleSubmit, reset } = useForm<IClassicDuelState>({
    defaultValues: initialState,
  });

  const onSubmit: SubmitHandler<IClassicDuelState> = useCallback(
    (data) => {
      dispatch(settingsActions.setClassicDuel(data));
      const remainingTime = data.classicDuel.roundTime.min * 60 + data.classicDuel.roundTime.sec;
      dispatch(teamActions.setPlayerRemainingTime({ player: 'player1', remainingTime }));
      dispatch(teamActions.setPlayerRemainingTime({ player: 'player2', remainingTime }));
      dispatch(
        settingsActions.setPartialDuel({
          player: 'player1',
          settings: { min: data.classicDuel.roundTime.min, sec: data.classicDuel.roundTime.sec },
        }),
      );
      dispatch(
        settingsActions.setPartialDuel({
          player: 'player2',
          settings: { min: data.classicDuel.roundTime.min, sec: data.classicDuel.roundTime.sec },
        }),
      );
      dispatch(teamActions.updateTimerKeys());
      params.set('game_mode', 'classic game');
      setParams(params);
      toggleFlyout();
    },
    [dispatch, params, setParams, toggleFlyout],
  );

  const onConfirmReset = useCallback(() => {
    dispatch(settingsActions.resetClassicDuel());
    toggleConfirmation();
    dispatch(teamActions.updateTimerKeys());
    reset(ClassicDuelInitialState);
  }, [dispatch, reset, toggleConfirmation]);

  return (
    <>
      <S.Form onSubmit={handleSubmit(onSubmit)}>
        <Accordion title="game timer">
          <S.AccordionElementsContainer>
            <Input
              variant="time"
              label="round time"
              minProps={{
                ...register('classicDuel.roundTime.min', { min: 0, valueAsNumber: true }),
                min: 0,
              }}
              secProps={{
                ...register('classicDuel.roundTime.sec', { min: 0, valueAsNumber: true }),
                min: 0,
              }}
            />
            <Input
              variant="toggle"
              label="End of Round Sound"
              {...register('classicDuel.isEnabledEndOfRoundSound')}
              defaultChecked={initialState.classicDuel.isEnabledEndOfRoundSound}
            />
          </S.AccordionElementsContainer>
        </Accordion>

        <S.Line />

        <Accordion
          title="pause"
          $bgColor={theme.colors.active_accordion}
        >
          <S.AccordionElementsContainer>
            <Input
              variant="time"
              label="pause time"
              minProps={{
                ...register('pause.pauseTime.min', { min: 0, valueAsNumber: true }),
                min: 0,
              }}
              secProps={{
                ...register('pause.pauseTime.sec', { min: 0, valueAsNumber: true }),
                min: 0,
              }}
            />

            <Input
              variant="toggle"
              label="Start blinking"
              {...register('pause.isStartBlinking')}
              defaultChecked={initialState.pause.isStartBlinking}
            />

            <Input
              variant="toggle"
              label="End of Pause Sound"
              {...register('pause.isEnabledEndOfPauseSound')}
              defaultChecked={initialState.pause.isEnabledEndOfPauseSound}
            />
          </S.AccordionElementsContainer>
        </Accordion>

        <S.Line />
        <Accordion title="Voters' timer">
          <S.AccordionElementsContainer>
            <Input
              variant="time"
              label="Voter's time"
              minProps={{
                ...register('votersTimer.pauseTime.min', { min: 0, valueAsNumber: true }),
                min: 0,
              }}
              secProps={{
                ...register('votersTimer.pauseTime.sec', { min: 0, valueAsNumber: true }),
                min: 0,
              }}
            />

            <Input
              variant="toggle"
              label="Start blinking"
              {...register('votersTimer.isStartBlinking')}
              defaultChecked={initialState.votersTimer.isStartBlinking}
            />

            <Input
              variant="toggle"
              label="End of Pause Sound"
              {...register('votersTimer.isEnabledEndOfPauseSound')}
              defaultChecked={initialState.votersTimer.isEnabledEndOfPauseSound}
            />
          </S.AccordionElementsContainer>
        </Accordion>

        <S.ButtonsContainer>
          <Button
            variant="outline"
            $size="large"
            type="button"
            onClick={toggleConfirmation}
          >
            reset settings
          </Button>
          <Button
            variant="primary"
            $size="large"
            type="submit"
          >
            apply
          </Button>
        </S.ButtonsContainer>
      </S.Form>
      <Confirmation
        description={RESET_SETTINGS_CONFIRMATION}
        confirmText={RESET_SETTINGS_CONFIRM_TEXT}
        abortText={RESET_SETTINGS_ABORT_TEXT}
        title="reset settings"
        isActive={isActive}
        onConfirm={onConfirmReset}
        onClose={toggleConfirmation}
        onAbort={toggleConfirmation}
      />
    </>
  );
};

export default ClassicDuelForm;
